import { useState, useEffect, useRef } from "react";
import {
  Tag,
  Button,
  Divider,
  Flex,
  Carousel,
  Spin,
  Typography,
} from "antd";
import {
  EyeOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import axiosInstance from "../api/axiosInstance";
import DynamicGalleryGrid from "./DynamicGalleryGrid";
import { useNavigate } from "react-router-dom";
import { useCity } from "../context/CityContext";

const { Title, Paragraph } = Typography;

const TravelSection = () => {
  const [tours, setTours] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedTour, setSelectedTour] = useState(null);
  const [leftOffset, setLeftOffset] = useState(0);

  const carouselRef = useRef(null);
  const containerRef = useRef(null);

  const navigate = useNavigate();

  // ==========================================
  // SELECTED CITY
  // ==========================================
  const { selectedCity } = useCity();

  // ==========================================
  // CITY NAME
  // ==========================================
  const cityName = selectedCity?.name || "Dubai";

  // ==========================================
  // UPDATE ARROW POSITION
  // ==========================================
  const updateArrowPosition = (index) => {
    if (!carouselRef.current || !containerRef.current) return;

    const slides =
      carouselRef.current.innerSlider?.list?.querySelectorAll(
        ".slick-slide"
      );

    if (!slides || slides.length === 0) return;

    const containerRect =
      containerRef.current.getBoundingClientRect();

    const activeSlide = slides[index];

    if (!activeSlide) return;

    const slideRect =
      activeSlide.getBoundingClientRect();

    const center =
      slideRect.left -
      containerRect.left +
      slideRect.width / 2;

    setLeftOffset(`${center}px`);
  };

  // ==========================================
  // FETCH CITY + GROUP WISE TOURS
  // ==========================================
  useEffect(() => {
    const fetchTours = async () => {
      // No city selected
      if (!selectedCity?.slug) {
        setTours([]);
        setSelectedTour(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // ==========================================
        // PRODUCT GROUP
        // ==========================================
        const groupName =
          "top_5_viral_things_everyone’s_doing";

        // ==========================================
        // API
        // ==========================================
        const res = await axiosInstance.get(
          "/product-details",
          {
            params: {
              // PRODUCT GROUP FILTER
              "filters[product_groups][name][$eq]":
                groupName,

              // CITY FILTER
              "filters[city][slug][$eq]":
                selectedCity.slug,

              // Fields
              "fields[0]": "title",
              "fields[1]": "slug",
              "fields[2]": "final_price",
              "fields[3]": "old_price",
              "fields[4]": "save_price",
              "fields[5]": "duration",

              // Images
              "populate[images][fields][0]":
                "url",

              "populate[images][fields][1]":
                "name",

              // Sub Product
              "populate[sub_product][fields][0]":
                "slug",

              // City
              "populate[city][fields][0]":
                "name",

              "populate[city][fields][1]":
                "slug",

              // Product Group
              "populate[product_groups][fields][0]":
                "name",

              // ==========================================
              // YOUR EXISTING DETAIL POPULATES
              // ==========================================

              "populate[Tour_overview][populate]":
                "itinerary_stop",

              "populate[itinerary][populate][Days][populate][image][fields]":
                "id,url,name,alternativeText,caption",

              "populate[itinerary][populate][Days][populate][transfer_info][populate][vehicle_image][fields]":
                "id,url,name,alternativeText,caption",

              "populate[itinerary][populate][Days][populate][hotel_info][populate][hotel_image][fields]":
                "id,url,name,alternativeText,caption",

              "populate[itinerary][populate][Days][populate][departure_pricing]":
                "*",

              "populate[itinerary][populate][Days][populate][activity][populate][activity_image][fields]":
                "id,url,name,alternativeText,caption",

              "populate[booking_info][populate]":
                "travellers",

              "populate[FAQ][populate]":
                "*",

              "populate[Gallery][populate][images][fields]":
                "id,url,name,alternativeText,caption",

              "populate[sections][populate]":
                "*",

              "populate[review_section][populate][reviews][populate][review_image][fields]":
                "id,url,name,alternativeText,caption",

              "populate[review_section][populate][reviews][populate][reviewer_image][fields]":
                "id,url,name,alternativeText,caption",

              "populate[review_section][populate][rating_levels]":
                "*",
            },
          }
        );

        const data = res.data.data || [];



        setTours(data);

        // Select first tour
        if (data.length > 0) {
          setSelectedIndex(0);
          setSelectedTour(data[0]);
        } else {
          setSelectedIndex(0);
          setSelectedTour(null);
        }
      } catch (error) {
        console.error(
          "Error fetching city viral tours:",
          error
        );

        setTours([]);
        setSelectedTour(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [selectedCity?.slug]);

  // ==========================================
  // UPDATE SELECTED TOUR
  // ==========================================
  useEffect(() => {
    if (tours.length > 0) {
      setSelectedTour(
        tours[selectedIndex] || tours[0]
      );
    }
  }, [selectedIndex, tours]);

  // ==========================================
  // UPDATE ARROW
  // ==========================================
  useEffect(() => {
    if (tours.length > 0) {
      setTimeout(() => {
        updateArrowPosition(selectedIndex);
      }, 100);
    }
  }, [tours, selectedIndex]);

  // ==========================================
  // RESIZE
  // ==========================================
  useEffect(() => {
    const handleResize = () => {
      updateArrowPosition(selectedIndex);
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );
    };
  }, [selectedIndex]);

  // ==========================================
  // CAROUSEL CHANGE
  // ==========================================
  const handleChange = (_, next) => {
    setSelectedIndex(next);

    setTimeout(() => {
      updateArrowPosition(next);
    }, 50);
  };

  // ==========================================
  // TOUR CLICK
  // ==========================================
  const handleClick = (index) => {
    setSelectedIndex(index);

    setTimeout(() => {
      updateArrowPosition(index);
    }, 50);
  };

  // ==========================================
  // LOADING
  // ==========================================
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spin
          size="large"
          tip={`Loading Top Viral Tours in ${cityName}...`}
        />
      </div>
    );
  }

  // ==========================================
  // NO DATA
  // ==========================================
  if (!loading && tours.length === 0) {
    return (
      <div className="container py-8">
        <div className="text-center text-gray-500">
          No viral tours available in {cityName}.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ==========================================
          HEADER
      ========================================== */}
      <div className="container">
        <div className="flex justify-between items-center mb-2 font-medium">
          <div>
            <Title
              className="!font-medium !text-danger !text-2xl !mb-1"
              level={2}
            >
              <span className="text-primary">
                Top{" "}
                <strong className="!font-bold !text-danger">
                  5
                </strong>{" "}
                Viral Things
              </span>{" "}
              Everyone’s Doing in {cityName}
            </Title>

            <Paragraph className="!mb-0">
              Culture, nature, thrills, and
              record-breaking experiences
            </Paragraph>
          </div>
        </div>

        {/* ==========================================
            CAROUSEL
        ========================================== */}
        <div
          ref={containerRef}
          className="grid grid-cols-1 gap-5"
        >
          <Carousel
            ref={carouselRef}
            dots={false}
            infinite={tours.length > 5}
            slidesToShow={Math.min(
              5,
              tours.length
            )}
            slidesToScroll={1}
            autoplay={false}
            beforeChange={handleChange}
            className="
              ViralThings
              no-slick-after
              [&_.slick-list]:!py-5
              [&_.slick-dots]:!static
              [&_.slick-dots]:!mt-6
              [&_.slick-dots]:!flex
              [&_.slick-dots]:!justify-center
              [&_.slick-dots_li]:!h-3
              [&_.slick-dots_li]:!w-3
              [&_.slick-dots_li_button]:!p-0
              [&_.slick-dots_li_button:before]:!text-transparent
              [&_.slick-dots_li_button]:!w-3
              [&_.slick-dots_li_button]:!h-3
              [&_.slick-dots_li_button]:!rounded-full
              [&_.slick-dots_li_button]:!bg-primary/40
              [&_.slick-dots_li.slick-active_button]:!bg-primary
            "
            responsive={[
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: Math.min(
                    3,
                    tours.length
                  ),
                },
              },
              {
                breakpoint: 768,
                settings: {
                  slidesToShow: Math.min(
                    2,
                    tours.length
                  ),
                },
              },
              {
                breakpoint: 480,
                settings: {
                  slidesToShow: 1,
                },
              },
            ]}
          >
            {tours.map((item, i) => {
              const imageUrl =
                item.images?.[0]?.url;

              return (
                <div
                  key={item.id || i}
                  className="px-2"
                >
                  <div
                    onClick={() =>
                      handleClick(i)
                    }
                    className={`relative rounded-2xl overflow-hidden shadow cursor-pointer transition-transform duration-300 ${selectedIndex === i
                        ? "shadow-custom scale-105"
                        : "hover:scale-105"
                      }`}
                  >
                    {imageUrl && (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${imageUrl}`}
                        alt={
                          item.title ||
                          "Tour"
                        }
                        className="w-full h-[30rem] lg:h-[23rem] object-cover"
                      />
                    )}

                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,25,118,0)_1.61%,#021976_100%)] p-4 flex flex-col justify-end">
                      <h4 className="text-white font-medium text-[16px] mb-1">
                        {item.title}
                      </h4>

                      <p className="text-white text-xs leading-snug font-medium">
                        {item.desc ||
                          item.description ||
                          ""}
                      </p>
                    </div>

                    {selectedIndex === i && (
                      <div className="hidden md:block absolute -bottom-[18px] left-1/2 -translate-x-1/2 w-4 h-4 bg-white border border-blue-200 rotate-45" />
                    )}
                  </div>
                </div>
              );
            })}
          </Carousel>
        </div>
      </div>

      {/* ==========================================
          SELECTED TOUR DETAILS
      ========================================== */}
      <div className="bg-lightSky-100">
        <div className="relative my-8 bg-blueBorder">
          <div className="relative container">
            <div
              className="absolute -top-[8px] w-4 h-4 bg-white rotate-45 transition-all duration-300 border-t border-l border-blueBorder"
              style={{
                left: leftOffset,
                transform:
                  "translateX(-50%) rotate(45deg)",
              }}
            />
          </div>

          <Divider className="bg-blueBorder" />
        </div>

        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

            {/* LEFT CONTENT */}
            <div className="w-full md:w-[80%] lg:w-96">

              <div className="mt-3 mb-2 text-xs font-normal">
                Tour Type

                <Tag className="bg-blue-100 text-black font-medium rounded border-0 text-xs ml-2 py-1">
                  {selectedTour?.tourType ||
                    selectedTour?.type ||
                    "Standard"}
                </Tag>
              </div>

              <div className="font-medium text-black text-lg leading-snug">
                {selectedTour?.title}
              </div>

              {/* PRICE */}
              <div className="flex items-baseline gap-2 mt-3">
                <span className="font-bold text-xl">
                  AED{" "}
                  {selectedTour?.final_price ??
                    selectedTour?.price ??
                    0}
                </span>

                {selectedTour?.old_price && (
                  <span className="line-through text-[15px]">
                    AED{" "}
                    {selectedTour.old_price}
                  </span>
                )}
              </div>

              {selectedTour?.save_price && (
                <p className="text-sm font-medium">
                  SAVE{" "}
                  {selectedTour.save_price}
                </p>
              )}

              {/* DURATION + RATING */}
              <div className="flex items-center gap-4 mt-3 text-sm">

                <span className="text-primary font-medium">
                  {selectedTour?.duration ||
                    "N/A"}
                </span>

                <Flex
                  align="center"
                  className="text-danger font-medium"
                >
                  <div className="text-xl">
                    ★
                  </div>

                  <div className="text-xs ml-1">
                    {selectedTour?.rating ||
                      "4.9 (523)"}
                  </div>
                </Flex>

              </div>

              {/* BUTTONS */}
              <Flex
                className="flex mt-6"
                gap={16}
              >
                <Button
                  type="danger"
                  size="small"
                  icon={
                    <EyeOutlined className="text-base" />
                  }
                  className="h-full font-medium flex-1 py-3 text-xs rounded-tl-[12px] rounded-bl-none rounded-tr-none rounded-br-[12px] bg-danger hover:bg-red-700 border-none rounded-md text-white w-full"
                  onClick={() => {

                    if (!selectedTour) return;

                    const subSlug =
                      selectedTour
                        .sub_product?.slug ||
                      "guided-tours";

                    const detailSlug =
                      selectedTour.slug ||
                      selectedTour.title
                        ?.toLowerCase()
                        .replace(
                          /\s+/g,
                          "-"
                        ) ||
                      "details";

                    navigate(
                      `/${subSlug}/${detailSlug}`
                    );
                  }}
                >
                  View Details
                </Button>

                <Button
                  size="middle"
                  type="default"
                  icon={
                    <PhoneOutlined className="text-base rotate-90" />
                  }
                  className="h-full font-medium flex-1 py-3 text-xs rounded-tl-[12px] rounded-bl-none rounded-tr-none rounded-br-[12px] border-gray-400 text-gray-500 rounded-lg"
                >
                  Call US
                </Button>
              </Flex>
            </div>

            {/* RIGHT IMAGE GRID */}
            <DynamicGalleryGrid
              selectedTour={selectedTour}
            />

          </div>
        </div>

        <Divider className="bg-blueBorder !mb-0" />
      </div>
    </div>
  );
};

export default TravelSection;